import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ApiService } from "src/app/shared/services/api.service";
import { Cart } from "../../q-commerce/models/cart.model";
import { Variant } from "../../q-commerce/models/category-group.model";
import { Merchant } from "../models/merchant.model";
import { MainCategory } from "../../q-commerce/models/main-category.model";
import { eDeliveryApiColletions } from "src/app/core/constants/api_collection";

@Injectable({
  providedIn: "root"
})
export class EDeliveryService {
  // BehaviorSubject to hold the selected booking type
  bookingType = new BehaviorSubject<number>(0);

  // Observable to expose the selected booking type
  bookingType$ = this.bookingType.asObservable();

  // Use a BehaviorSubject to store the cart items
  private cartItems = new BehaviorSubject<Cart[]>([]);

  // Observable to expose the cart items
  cartItems$ = this.cartItems.asObservable();

  // Subject to hold the selected main category ID
  private selectedMainCategorySubject = new Subject<MainCategory>();

  // Observable to expose the selected main category ID
  selectedMainCategory$ = this.selectedMainCategorySubject.asObservable();

  // BehaviourSubject to hold the selected cart merchant
  private selectedCartMerchantSubject = new BehaviorSubject<Merchant | null>(null);

  //Observable to expose the selected cart merchant
  selectedCartMerchant$ = this.selectedCartMerchantSubject.asObservable();

  // BehaviourSubject to hold the city setting
  private citySettingSubject = new BehaviorSubject<any>(null);

  // Observable to expose the city setting
  citySettingSubject$ = this.citySettingSubject.asObservable();

  // Subject to hold the merchant setting
  private merchantSettingSubject = new BehaviorSubject<any>(null);

  // Observable to expose the merchant setting
  merchantSettingSubject$ = this.merchantSettingSubject.asObservable();

  constructor(private apiService: ApiService) {}

  /**
   * Fetches the list of main categories using the ApiService.
   * Subscribes to the service's observable to receive the category data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the category data.
   */
  getMainCategoryList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: eDeliveryApiColletions.mainCategory, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of merchants using the ApiService.
   * Subscribes to the service's observable to receive the merchant data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the merchant data.
   */
  getMerchantList(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const url = `${eDeliveryApiColletions.merchantList}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the cart data using the ApiService.
   * Subscribes to the service's observable to receive the cart data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the cart data.
   */
  getCart(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${eDeliveryApiColletions.cart}`;
      this.apiService.post({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Adds a merchant to the user's favourite list using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  addFavouriteMerchant(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const url = `${eDeliveryApiColletions.favouriteMerchant}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Removes a merchant from the user's favourite list using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @param merchantId The ID of the merchant to be removed from the favourite list.
   * @returns An observable of the response data.
   */
  removeFavouriteMerchant(parameters: any, merchantId: string): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const url = `${eDeliveryApiColletions.favouriteMerchant}/${merchantId}`;
      this.apiService.delete({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Places an order using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  placeOrder(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${eDeliveryApiColletions.booking}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response) {
            observer.next(response);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Adds a product variant to the cart. If the product variant is already present in the cart,
   * the quantity will be incremented by the given quantity, up to the maximum allowed in the cart.
   * Otherwise, the product variant will be added with an initial quantity of 1.
   *
   * @param item - The cart item to add to the cart.
   * @param variant - The product variant to add to the cart.
   */
  addToCart(item: Cart, variant: Variant): void {
    const currentCart = this.cartItems.value.length ? [...this.cartItems.value] : [...JSON.parse(localStorage.getItem("deliveryCartItems") || "[]")];
    const existingItem = currentCart.find((cartItem) => {
      if (cartItem.modifiers && cartItem.modifiers.length > 0) {
        return cartItem.productId === item.productId && cartItem.variantId === item.variantId && JSON.stringify(cartItem.modifiers) === JSON.stringify(item.modifiers);
      }
      return cartItem.productId === item.productId && cartItem.variantId === item.variantId;
    });
    if (existingItem) {
      existingItem.qty = Math.min(existingItem.qty + item.qty, variant.maxQtyAddInCart);
    } else {
      item.qty = Math.min(item.qty, variant.maxQtyAddInCart);
      currentCart.push(item);
    }
    localStorage.setItem("deliveryCartItems", JSON.stringify(currentCart));
    this.updateCartItems(currentCart);
  }

  /**
   * Removes a product variant from the cart.
   * If the product variant has modifiers, it matches the variant and its modifiers.
   * Otherwise, it matches solely based on the product and variant IDs.
   * Updates the cart in local storage and notifies subscribers of the change.
   *
   * @param productId - The ID of the product to remove from the cart.
   * @param variant - The variant (and its modifiers, if any) to remove from the cart.
   */
  removeFromCart(productId: string, variant: any): void {
    const currentCart: Cart[] = JSON.parse(localStorage.getItem("deliveryCartItems") || "[]").filter((cartItem: Cart) => {
      if (cartItem.modifiers && cartItem.modifiers.length > 0) {
        return !(cartItem.productId === productId && cartItem.variantId === variant.variantId && JSON.stringify(cartItem.modifiers) === JSON.stringify(variant.modifiers));
      }
      return !(cartItem.productId === productId && cartItem.variantId === (typeof variant === "object" ? variant.variantId : variant));
    });
    localStorage.setItem("deliveryCartItems", JSON.stringify(currentCart));
    this.updateCartItems(currentCart);
  }

  /**
   * Updates the quantity of a product variant in the cart. If the product variant is in the cart, the quantity will be updated to the given quantity,
   * up to the maximum allowed in the cart (maxQtyAddInCart). If the product variant is not in the cart, the cart remains unchanged.
   * @param productId The ID of the product to update in the cart.
   * @param variant The product variant to update in the cart.
   * @param quantity The new quantity of the product variant in the cart.
   * @param maxQtyAddInCart The maximum quantity that can be added to the cart for this product variant.
   */
  updateQuantity(productId: string, variant: any, quantity: number, maxQtyAddInCart: number): void {
    const currentCart: Cart[] = JSON.parse(localStorage.getItem("deliveryCartItems") || "[]");
    const itemIndex = currentCart.findIndex((cartItem) => {
      if (cartItem.modifiers && cartItem.modifiers.length > 0) {
        return cartItem.productId === productId && cartItem.variantId === variant.variantId && JSON.stringify(cartItem.modifiers) === JSON.stringify(variant.modifiers);
      }
      return cartItem.productId === productId && cartItem.variantId === (typeof variant === "object" ? variant.variantId : variant);
    });
    if (itemIndex !== -1) {
      // Update the quantity of the item (ensure it doesn't exceed maxQtyAddInCart)
      currentCart[itemIndex].qty = Math.min(quantity, maxQtyAddInCart);
      // Emit the updated cart
      localStorage.setItem("deliveryCartItems", JSON.stringify(currentCart));
      this.updateCartItems(currentCart);
    }
  }

  /**
   * Gets the current state of the cart, which is an array of `Cart` items.
   * @returns The current state of the cart as an array of `Cart` items.
   */
  getCartItems(): Cart[] {
    return this.cartItems.value;
  }

  /**
   * Updates the current state of the cart with the given array of `Cart` items.
   * This will cause all subscribers to the `cartItems$` observable to receive the new cart items.
   * @param cartItems The array of `Cart` items to update the cart with.
   */
  updateCartItems(cartItems: Cart[]): void {
    this.cartItems.next(cartItems);
  }

  /**
   * Checks if a business is available for ordering using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  checkDeliveryBusiness(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${eDeliveryApiColletions.checkBusiness}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the product details using the ApiService.
   * Subscribes to the service's observable to receive the product details, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @param productId The ID of the product to fetch details for.
   * @returns An observable of the response data.
   */
  getProductDetails(parameters: any, productId: string): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${eDeliveryApiColletions.productDetails}/${productId}`;
      this.apiService.get({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Updates the selected main category observable with the given main category.
   * This will cause all subscribers to the selectedMainCategory$ observable to receive the new main category.
   * @param mainCategory The main category to select as the current main category.
   */
  updateSelectedMainCategory(mainCategory: MainCategory): void {
    this.selectedMainCategorySubject.next(mainCategory);
  }

  /**
   * Updates the selected cart merchant observable with the given merchant.
   * This will cause all subscribers to the selectedCartMerchant$ observable to receive the new merchant.
   * @param merchant The merchant to select as the current cart merchant.
   */
  updateSelectedCartMerchant(merchant: Merchant): void {
    this.selectedCartMerchantSubject.next(merchant);
  }

  /**
   * Updates the selected booking type observable with the given booking type.
   * This will cause all subscribers to the bookingType$ observable to receive the new booking type.
   * @param bookingType The booking type to select as the current booking type.
   */
  updateBookingType(bookingType: number): void {
    this.bookingType.next(bookingType);
  }

  /**
   * Updates the city setting observable with the given city setting.
   * This will cause all subscribers to the citySettingSubject$ observable to receive the new city setting.
   * @param citySetting The city setting to be updated.
   */

  updateCitySetting(citySetting: any): void {
    this.citySettingSubject.next(citySetting);
  }

  /**
   * Updates the merchant setting observable with the given merchant setting.
   * This will cause all subscribers to the merchantSettingSubject$ observable to receive the new merchant setting.
   * @param merchantSetting The merchant setting to be updated.
   */
  updateMerchantSetting(merchantSetting: any): void {
    this.merchantSettingSubject.next(merchantSetting);
  }

  /**
   * Fetches the list of product filters based on the provided category ID or group ID.
   * Constructs the query parameters using the provided ID, name, and viewAll flag.
   * Subscribes to the observable to receive the filter data; emits the data through the observer if successful,
   * otherwise emits false.
   *
   * @param id - The ID of the category or group to fetch filters for. If viewAll is true, it is treated as groupId.
   * @param name - Optional name to filter the results by.
   * @param viewAll - Flag to indicate if all filters should be viewed, default is false.
   * @returns An observable containing the product filter data or false on error.
   */
  getProductFilterList(id: string, businessType: number, name?: string, viewAll: boolean = false, isBrand: boolean = false): Observable<any> {
    const parameters: any = {
      businessType: businessType,
      isShowErrorToast: false,
      isShowSuccessToast: false
    };
    if (id && !viewAll && !isBrand) {
      parameters["categoryId"] = id;
    } else if (id && viewAll && !isBrand) {
      parameters["groupId"] = id;
    } else if (id && !viewAll && isBrand) {
      parameters["brandId"] = id;
    }
    if (name) {
      parameters["name"] = name;
    }
    return new Observable<any>((observer) => {
      this.apiService.get({ url: eDeliveryApiColletions.productFilterList, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false); // Return false if there's an error
          observer.complete();
        }
      });
    });
  }
}
